import {
  PricingBreakdown,
  PricingCombination,
  PricingCombinationOption,
  PricingTier,
  PricingHistoryRow,
  PricingKeyRow,
  PricingMeteredItem,
  PricingOtherValue,
  PricingPack,
  PricingVersion,
  PricingVersionEditFormValues,
  PricingVersionRow,
} from "./types";

/*---------------------------------------------------------------------------------------
    VERZIJE
-----------------------------------------------------------------------------------------*/

export const transformPricingVersionRow = (res: any): PricingVersionRow => ({
  id: res.id,
  validFrom: res.validFrom,
  note: res.note ?? null,
  isDraft: res.isDraft,
  isEffective: res.isEffective,
  created: res.created,
  userCreatedId: res.userCreatedId ?? null,
  userCreatedFullName: res.userCreatedFullName ?? null,
});

export const transformPricingCombination = (res: any): PricingCombination => ({
  id: res.id,
  keyCode: res.keyCode,
  description: res.description ?? null,
  sortOrder: res.sortOrder,
  packCodes: res.packCodes ?? [],
  price: res.price,
});

export const transformPricingTier = (res: any): PricingTier => ({
  id: res.id,
  fromVehicle: res.fromVehicle,
  toVehicle: res.toVehicle ?? null,
  ratePercent: res.ratePercent,
});

export const transformPricingMeteredItem = (res: any): PricingMeteredItem => ({
  code: res.code,
  name: res.name,
  costKeyCode: res.costKeyCode,
  price: res.price,
  cost: res.cost,
  margin: res.margin,
  isBelowCost: res.isBelowCost,
  measuredCost: res.measuredCost
    ? {
        currency: res.measuredCost.currency,
        samples: res.measuredCost.samples,
        average: res.measuredCost.average,
        min: res.measuredCost.min,
        max: res.measuredCost.max,
      }
    : null,
});

export const transformPricingOtherValue = (res: any): PricingOtherValue => ({
  code: res.code,
  name: res.name,
  valueKind: res.valueKind,
  value: res.value,
});

export const transformPricingVersion = (res: any): PricingVersion => ({
  id: res.id,
  validFrom: res.validFrom,
  note: res.note ?? null,
  isDraft: res.isDraft,
  combinations: (res.combinations ?? []).map(transformPricingCombination),
  priceTiers: (res.priceTiers ?? []).map(transformPricingTier),
  meteredItems: (res.meteredItems ?? []).map(transformPricingMeteredItem),
  otherValues: (res.otherValues ?? []).map(transformPricingOtherValue),
});

export const transformPricingPack = (res: any): PricingPack => ({
  id: res.id,
  code: res.code,
  name: res.name,
  isCore: res.isCore,
  sortOrder: res.sortOrder,
  isActive: res.isActive,
});

export const transformPricingKeyRow = (res: any): PricingKeyRow => ({
  id: res.id,
  code: res.code,
  name: res.name,
  valueKind: res.valueKind,
  isOverridable: res.isOverridable,
  costKeyCode: res.costKeyCode ?? null,
  sortOrder: res.sortOrder,
  isActive: res.isActive,
});

export const transformPricingCombinationOption = (res: any): PricingCombinationOption => ({
  keyCode: res.keyCode,
  sortOrder: res.sortOrder,
  packCodes: res.packCodes ?? [],
});

export const transformPricingBreakdown = (res: any): PricingBreakdown => ({
  keyCode: res.keyCode,
  onDate: res.onDate,
  standard: res.standard,
  afterScheme: res.afterScheme,
  effective: res.effective,
  discountPercent: res.discountPercent,
});

export const transformPricingHistoryRow = (res: any): PricingHistoryRow => ({
  id: res.id,
  comment: res.comment,
  entityId: res.entityId ?? null,
  changes: res.changes ?? null,
  userId: res.userId,
  created: res.created,
});

/*---------------------------------------------------------------------------------------
    SPREMANJE

    Ključevi kombinacija (`vozilo.*`) se NE šalju — backend ih izvodi iz šifara packova,
    pa ista kombinacija uvijek dobije isti ključ.
-----------------------------------------------------------------------------------------*/

export const transformPricingVersionOnSave = (formValues: PricingVersionEditFormValues): any => ({
  validFrom: formValues.validFrom,
  note: formValues.note,
  values: Object.fromEntries(
    Object.entries(formValues.values)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([code, value]) => [code, Number(value)])
  ),
  combinations: formValues.combinations.map((combination) => ({
    packCodes: combination.packCodes,
    price: Number(combination.price ?? 0),
    description: combination.description,
    sortOrder: combination.sortOrder,
  })),
  priceTiers: formValues.priceTiers.map((band) => ({
    fromVehicle: Number(band.fromVehicle ?? 0),
    toVehicle: band.toVehicle === null ? null : Number(band.toVehicle),
    ratePercent: Number(band.ratePercent ?? 0),
  })),
});

/*---------------------------------------------------------------------------------------
    VERZIJA -> OBRAZAC
-----------------------------------------------------------------------------------------*/

export const transformPricingVersionToForm = (
  version: PricingVersion
): PricingVersionEditFormValues => ({
  validFrom: version.validFrom,
  note: version.note,
  values: {
    ...Object.fromEntries(version.otherValues.map((v) => [v.code, v.value])),
    ...Object.fromEntries(version.meteredItems.flatMap((m) => [
      [m.code, m.price],
      [m.costKeyCode, m.cost],
    ])),
  },
  combinations: version.combinations.map((combination) => ({
    packCodes: combination.packCodes,
    price: combination.price,
    description: combination.description,
    sortOrder: combination.sortOrder,
  })),
  priceTiers: version.priceTiers.map((band) => ({
    fromVehicle: band.fromVehicle,
    toVehicle: band.toVehicle,
    ratePercent: band.ratePercent,
  })),
});
